import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { faEdit, faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  ButtonIcon,
  ButtonText,
  Table,
  useToast
} from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  fetchJournalSpecificKeywordFormSchema,
  fetchReviewerExpertiseSuggestionKeywords,
  fetchUserJournalSpecificKeywords,
  IUserJournalSpecificKeywordGroup,
  useCreateJournalSpecificKeywordMutation,
  useDeleteJournalSpecificKeywordMutation,
  useUpdateJournalSpecificKeywordMutation
} from 'src/api/signUpMutation';
import { FieldLabel } from 'src/components/Field';
import { CustomSearchSelect } from 'src/components/UserRegistrationSteps/StepThree/CustomSearchSelect';
import { NewKeywordModal } from 'src/components/UserRegistrationSteps/StepThree/NewKeywordModal';
import {
  Modal as ChakraModal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay
} from '@chakra-ui/react';

type AffiliatedJournal = {
  id: string;
  name: string;
  shortname?: string;
};

type JournalSpecificKeywordRow =
  IUserJournalSpecificKeywordGroup['keywords'][number];

const getKeywordFieldDisplayValue = (
  keyword: JournalSpecificKeywordRow,
  dataKey: string
) => {
  const value = keyword[dataKey as keyof JournalSpecificKeywordRow];
  return typeof value === 'string' && value.trim() ? value : '-';
};

type JournalSpecificKeywordForm = {
  journalSpecificKeywords: Array<{
    keyword?: { termId: string; name: string } | null;
    scandinavianKeyword?: { termId: string; name: string } | null;
  }>;
};

type ModalMode = 'add' | 'edit';

type ModalState = {
  isOpen: boolean;
  mode: ModalMode;
  journal?: IUserJournalSpecificKeywordGroup;
  row?: JournalSpecificKeywordRow;
};

type NewKeywordState = {
  rowIndex: number;
  fieldName: 'keyword' | 'scandinavianKeyword';
  fieldLabel: string;
} | null;

const buildCustomTermId = (fieldName: string, name: string) =>
  `custom_${fieldName}_${name.trim().toLowerCase().replace(/\s+/g, '_')}`;

const normalizeText = (value?: string) => value?.trim().toLowerCase() ?? '';

const formatLabelText = (value?: string) =>
  (value ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

const formatDate = (value?: string | Date) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  }).format(date);
};

const mapRowToFormRows = (
  row?: JournalSpecificKeywordRow
): JournalSpecificKeywordForm['journalSpecificKeywords'] => {
  if (!row) {
    return [{ keyword: null, scandinavianKeyword: null }];
  }

  return [
    {
      keyword: row.keyword
        ? {
            termId: row.keywordId ?? buildCustomTermId('keyword', row.keyword),
            name: row.keyword
          }
        : null,
      scandinavianKeyword: row.scandinavianKeyword
        ? {
            termId:
              row.scandinavianKeywordId ??
              buildCustomTermId('scandinavianKeyword', row.scandinavianKeyword),
            name: row.scandinavianKeyword
          }
        : null
    }
  ];
};

const JournalSpecificKeywordModal = ({
  userId,
  state,
  onClose,
  onSaved
}: {
  userId: string;
  state: ModalState;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const toast = useToast();
  const createMutation = useCreateJournalSpecificKeywordMutation();
  const updateMutation = useUpdateJournalSpecificKeywordMutation();
  const [newKeywordState, setNewKeywordState] = useState<NewKeywordState>(null);
  const initialFocusRef = useRef<HTMLDivElement | null>(null);
  const methods = useForm<JournalSpecificKeywordForm>({
    defaultValues: {
      journalSpecificKeywords: mapRowToFormRows(state.row)
    }
  });
  const {
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
    setError,
    clearErrors
  } = methods;
  const { fields } = useFieldArray({
    control,
    name: 'journalSpecificKeywords'
  });

  const submissionContextId = state.journal?.submissionContextId ?? '';
  const { data: schemaData, isLoading: isSchemaLoading } = useQuery({
    queryKey: ['journal-specific-keyword-form-schema', submissionContextId],
    queryFn: () =>
      submissionContextId
        ? fetchJournalSpecificKeywordFormSchema(submissionContextId)
        : null,
    enabled: state.isOpen && Boolean(submissionContextId)
  });

  const keywordConfig = schemaData?.documentSchema?.formFields?.find(
    (field: any) => field.name === 'JournalSpecificKeyword'
  );
  const scandinavianKeywordConfig =
    schemaData?.documentSchema?.formFields?.find(
      (field: any) => field.name === 'JournalSpecificScandinavianKeyword'
    );
  const isKeywordRequired =
    keywordConfig?.params?.validations?.required ?? false;
  const isScandinavianKeywordRequired =
    scandinavianKeywordConfig?.params?.validations?.required ?? false;
  const existingKeywordNames = useMemo(
    () =>
      new Set(
        (state.journal?.keywords ?? [])
          .filter(keyword => keyword.id !== state.row?.id)
          .flatMap(keyword => [
            normalizeText(keyword.keyword),
            normalizeText(keyword.scandinavianKeyword)
          ])
          .filter(Boolean)
      ),
    [state.journal?.keywords, state.row?.id]
  );

  useEffect(() => {
    if (!state.isOpen) return;

    reset({
      journalSpecificKeywords: mapRowToFormRows(state.row)
    });
    setNewKeywordState(null);
  }, [reset, state.isOpen, state.row]);

  const handleCellChange = (fieldName: string, value: any) => {
    clearErrors(fieldName as any);
    setValue(fieldName as any, value, {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  const handleNewKeywordClick = (
    rowIndex: number,
    fieldName: string,
    fieldLabel?: string
  ) => {
    setNewKeywordState({
      rowIndex,
      fieldName:
        fieldName === 'scandinavianKeyword' ? 'scandinavianKeyword' : 'keyword',
      fieldLabel: fieldLabel || fieldName
    });
  };

  const handleSaveNewKeyword = (keywordName: string) => {
    if (!newKeywordState) return;

    handleCellChange(
      `journalSpecificKeywords.${newKeywordState.rowIndex}.${newKeywordState.fieldName}`,
      {
        termId: buildCustomTermId(newKeywordState.fieldName, keywordName),
        name: keywordName
      }
    );
    setNewKeywordState(null);
  };

  const handleSave = async () => {
    const rows = getValues('journalSpecificKeywords') ?? [];
    const rowsToSave: typeof rows = [];
    let hasValidationErrors = false;
    const duplicateKeywordNames = new Set<string>();
    const duplicateScandinavianKeywordNames = new Set<string>();

    clearErrors('journalSpecificKeywords' as any);

    rows.forEach((row, index) => {
      const hasKeyword = Boolean(row.keyword?.name);
      const hasScandinavianKeyword = Boolean(row.scandinavianKeyword?.name);
      const hasAnyValue = hasKeyword || hasScandinavianKeyword;
      const normalizedKeyword = normalizeText(row.keyword?.name);
      const normalizedScandinavianKeyword = normalizeText(
        row.scandinavianKeyword?.name
      );

      if (
        !hasAnyValue &&
        !isKeywordRequired &&
        !isScandinavianKeywordRequired
      ) {
        return;
      }

      if (isKeywordRequired && !hasKeyword) {
        setError(`journalSpecificKeywords.${index}.keyword` as any, {
          type: 'validate',
          message: `${
            formatLabelText(keywordConfig?.params?.labels?.main) || 'Keyword'
          } is required`
        });
        hasValidationErrors = true;
      }

      if (isScandinavianKeywordRequired && !hasScandinavianKeyword) {
        setError(
          `journalSpecificKeywords.${index}.scandinavianKeyword` as any,
          {
            type: 'validate',
            message: `${
              formatLabelText(
                scandinavianKeywordConfig?.params?.labels?.main
              ) || 'Scandinavian keyword'
            } is required`
          }
        );
        hasValidationErrors = true;
      }

      if (
        hasKeyword &&
        (existingKeywordNames.has(normalizedKeyword) ||
          duplicateKeywordNames.has(normalizedKeyword))
      ) {
        setError(`journalSpecificKeywords.${index}.keyword` as any, {
          type: 'validate',
          message: 'Duplicate keyword is not allowed.'
        });
        hasValidationErrors = true;
      }

      if (
        hasScandinavianKeyword &&
        (existingKeywordNames.has(normalizedScandinavianKeyword) ||
          duplicateScandinavianKeywordNames.has(normalizedScandinavianKeyword))
      ) {
        setError(
          `journalSpecificKeywords.${index}.scandinavianKeyword` as any,
          {
            type: 'validate',
            message: 'This Scandinavian keyword has already been added'
          }
        );
        hasValidationErrors = true;
      }

      if (hasAnyValue) {
        rowsToSave.push(row);
      }

      if (normalizedKeyword) {
        duplicateKeywordNames.add(normalizedKeyword);
      }

      if (normalizedScandinavianKeyword) {
        duplicateScandinavianKeywordNames.add(normalizedScandinavianKeyword);
      }
    });

    if (hasValidationErrors) {
      return;
    }

    if (!rowsToSave.length) {
      setError('journalSpecificKeywords.0.keyword' as any, {
        type: 'validate',
        message: 'At least one keyword or Scandinavian keyword is required.'
      });
      return;
    }

    if (state.mode === 'edit' && state.row?.id) {
      const row = rowsToSave[0]!;
      const response = await updateMutation.mutateAsync({
        userId,
        submissionContextId,
        keywordId: state.row.id,
        payload: {
          keyword: row.keyword?.name,
          keywordId: row.keyword?.termId,
          scandinavianKeyword: row.scandinavianKeyword?.name,
          scandinavianKeywordId: row.scandinavianKeyword?.termId
        }
      });

      if (!response.isSuccess) {
        toast({
          variant: 'error',
          title: 'Error',
          description:
            response?.message?.message ||
            response?.message ||
            'Unable to update journal-specific keywords.',
          isClosable: true
        });
        return;
      }
    } else {
      const responses = await Promise.all(
        rowsToSave.map(row =>
          createMutation.mutateAsync({
            userId,
            submissionContextId,
            payload: {
              keyword: row.keyword?.name,
              keywordId: row.keyword?.termId,
              scandinavianKeyword: row.scandinavianKeyword?.name,
              scandinavianKeywordId: row.scandinavianKeyword?.termId
            }
          })
        )
      );
      const failedResponse = responses.find(response => !response.isSuccess);

      if (failedResponse) {
        toast({
          variant: 'error',
          title: 'Error',
          description:
            failedResponse?.message?.message ||
            failedResponse?.message ||
            'Unable to save journal-specific keywords.',
          isClosable: true
        });
        return;
      }
    }

    toast({
      variant: 'success',
      title:
        state.mode === 'edit'
          ? 'Journal-specific keywords updated'
          : 'Journal-specific keywords saved',
      isClosable: true
    });
    onSaved();
    onClose();
  };

  return (
    <ChakraModal
      isOpen={state.isOpen}
      onClose={onClose}
      initialFocusRef={initialFocusRef}
    >
      <ModalOverlay />
      <ModalContent maxWidth="auto" width="920px">
        <ModalCloseButton visibility="hidden" />
        <Box ref={initialFocusRef} tabIndex={-1} outline="none" />
        <Flex direction="column">
          <Flex
            direction="column"
            gap={3}
            padding={4}
            borderBottomWidth="1px"
            borderBottomColor="neutralPrimaryBorder"
            backgroundColor="surfaceSecondaryFill"
          >
            <Text textStyle="headingGroup">
              {state.mode === 'edit'
                ? 'Edit journal-specific keywords'
                : 'Add journal-specific keywords'}
            </Text>
            <Flex alignItems="center" gap={2} flexWrap="wrap">
              <Badge variant="interactive" size="context">
                Journal
              </Badge>
              <Text textStyle="headingItem">
                {state.journal?.journalName}
                {state.journal?.journalShortname
                  ? ` (${state.journal.journalShortname})`
                  : ''}
              </Text>
            </Flex>
          </Flex>

          <Flex direction="column" gap={5} padding={6}>
            <Flex direction="column" gap={3}>
              {isSchemaLoading ? (
                <Flex justifyContent="center" padding={6}>
                  <Spinner />
                </Flex>
              ) : (
                <Box overflowX="auto" width="100%">
                  <Table>
                    <Table.HeaderRow display="flex" gap={4}>
                      {keywordConfig && (
                        <Table.HeaderCell minWidth="260px" flex={1}>
                          <FieldLabel
                            label={keywordConfig.params.labels.main}
                            isRequired={
                              keywordConfig.params.validations.required
                            }
                            subText=""
                            helpText={keywordConfig.params.labels.subtitle}
                          />
                        </Table.HeaderCell>
                      )}
                      {scandinavianKeywordConfig && (
                        <Table.HeaderCell minWidth="260px" flex={1}>
                          <FieldLabel
                            label={scandinavianKeywordConfig.params.labels.main}
                            isRequired={
                              scandinavianKeywordConfig.params.validations
                                .required
                            }
                            subText=""
                            helpText={
                              scandinavianKeywordConfig.params.labels.subtitle
                            }
                          />
                        </Table.HeaderCell>
                      )}
                    </Table.HeaderRow>
                    <Table.Body>
                      {fields.map((field, index) => {
                        const keywordPath =
                          `journalSpecificKeywords.${index}.keyword` as const;
                        const scandinavianKeywordPath =
                          `journalSpecificKeywords.${index}.scandinavianKeyword` as const;
                        const keyword = watch(keywordPath);
                        const scandinavianKeyword = watch(
                          scandinavianKeywordPath
                        );
                        const keywordError = errors.journalSpecificKeywords?.[
                          index
                        ]?.keyword?.message as string | undefined;
                        const scandinavianKeywordError = errors
                          .journalSpecificKeywords?.[index]?.scandinavianKeyword
                          ?.message as string | undefined;

                        return (
                          <Table.Row
                            key={field.id}
                            display="flex"
                            gap={4}
                            alignItems="start"
                            _last={{ borderBottomWidth: 0 }}
                          >
                            {keywordConfig && (
                              <Table.RowCell minWidth="260px" flex={1}>
                                <CustomSearchSelect
                                  value={keyword || ''}
                                  onChange={handleCellChange}
                                  placeholder="keyword"
                                  searchMethod={
                                    fetchReviewerExpertiseSuggestionKeywords
                                  }
                                  fieldName={keywordPath}
                                  rowIndex={index}
                                  handleNewKeywordClick={handleNewKeywordClick}
                                  newKeywordFieldName="keyword"
                                  newKeywordLabel="keyword"
                                  isInvalid={Boolean(keywordError)}
                                  errorMessage={keywordError}
                                />
                              </Table.RowCell>
                            )}
                            {scandinavianKeywordConfig && (
                              <Table.RowCell minWidth="260px" flex={1}>
                                <CustomSearchSelect
                                  value={scandinavianKeyword || ''}
                                  onChange={handleCellChange}
                                  placeholder="scandinavian keyword"
                                  searchMethod={
                                    fetchReviewerExpertiseSuggestionKeywords
                                  }
                                  fieldName={scandinavianKeywordPath}
                                  rowIndex={index}
                                  handleNewKeywordClick={handleNewKeywordClick}
                                  newKeywordFieldName="scandinavianKeyword"
                                  newKeywordLabel="Scandinavian keyword"
                                  isInvalid={Boolean(scandinavianKeywordError)}
                                  errorMessage={scandinavianKeywordError}
                                />
                              </Table.RowCell>
                            )}
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>
                </Box>
              )}
            </Flex>

            <Flex justifyContent="flex-end" gap={3} paddingTop={5}>
              <Button variant="secondary" onClick={onClose}>
                Exit Without Saving
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={createMutation.isLoading || updateMutation.isLoading}
                isDisabled={
                  createMutation.isLoading || updateMutation.isLoading
                }
              >
                {createMutation.isLoading || updateMutation.isLoading
                  ? 'Saving...'
                  : 'Save Changes'}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </ModalContent>
      <NewKeywordModal
        isOpen={Boolean(newKeywordState)}
        onClose={() => setNewKeywordState(null)}
        onSave={handleSaveNewKeyword}
        selectedField={newKeywordState?.fieldLabel ?? 'keyword'}
        heading={
          newKeywordState?.fieldName === 'scandinavianKeyword'
            ? 'Create new Scandinavian keyword'
            : 'Create new keyword'
        }
        defaultValue=""
        subHeading="Describe your area of expertise."
        placeholderText="e.g., Nursing Education"
      />
    </ChakraModal>
  );
};

const JournalSpecificKeywordsSection = ({
  userId,
  journals = []
}: {
  userId: string;
  journals?: AffiliatedJournal[];
}) => {
  const toast = useToast();
  const deleteMutation = useDeleteJournalSpecificKeywordMutation();
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    mode: 'add'
  });
  const { data, isLoading } = useQuery({
    queryKey: ['user-journal-specific-keywords', userId],
    queryFn: () => fetchUserJournalSpecificKeywords(userId),
    enabled: Boolean(userId)
  });

  const journalSpecificKeywords = data?.data?.journalSpecificKeywords ?? [];
  const availableJournals = useMemo(() => {
    const groupsByContextId = new Map(
      journalSpecificKeywords.map(journal => [
        journal.submissionContextId,
        journal
      ])
    );

    journals.forEach(journal => {
      if (!groupsByContextId.has(journal.id)) {
        groupsByContextId.set(journal.id, {
          submissionContextId: journal.id,
          journalName: journal.name,
          journalShortname: journal.shortname ?? '',
          workspaceId: '',
          keywordFields: [],
          keywords: []
        });
      }
    });

    return Array.from(groupsByContextId.values());
  }, [journalSpecificKeywords, journals]);

  const handleDeleteRow = async (
    journal: IUserJournalSpecificKeywordGroup,
    keyword: JournalSpecificKeywordRow
  ) => {
    const response = await deleteMutation.mutateAsync({
      userId,
      submissionContextId: journal.submissionContextId,
      keywordId: keyword.id
    });

    if (!response.isSuccess) {
      toast({
        variant: 'error',
        title: 'Error',
        description:
          response?.message?.message ||
          response?.message ||
          'Unable to delete journal-specific keyword.',
        isClosable: true
      });
      return;
    }

    toast({
      variant: 'success',
      title: 'Journal-specific keyword deleted',
      isClosable: true
    });
  };

  return (
    <>
      {isLoading ? (
        <Flex justifyContent="center" padding={8}>
          <Spinner />
        </Flex>
      ) : (
        <Flex direction="column" gap={4}>
          {availableJournals.map(journal => (
            <Box
              key={journal.submissionContextId}
              backgroundColor="surfacePrimaryFill"
              borderWidth="1px"
              borderColor="neutralPrimaryBorder"
              borderRadius="8px"
              overflow="hidden"
              boxShadow="objectDefault"
            >
              <Flex
                alignItems="center"
                justifyContent="space-between"
                bgColor="actionSecondaryFill"
                px={2}
                py={2}
                gap={3}
                flexWrap="wrap"
              >
                <Flex alignItems="center" gap={2} flexWrap="wrap" minWidth={0}>
                  <Text textStyle="headingItem" noOfLines={2}>
                    {journal.journalName}
                    {journal.journalShortname
                      ? ` (${journal.journalShortname})`
                      : ''}
                  </Text>
                </Flex>
                <Flex flexShrink={0}>
                  <ButtonText
                    iconLeft={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() =>
                      setModalState({
                        isOpen: true,
                        mode: 'add',
                        journal
                      })
                    }
                  >
                    Add
                  </ButtonText>
                </Flex>
              </Flex>
              <Box overflowX="auto" width="100%">
                <Table>
                  <Table.HeaderRow>
                    {(journal.keywordFields?.length
                      ? journal.keywordFields
                      : [{ dataKey: 'keyword', label: 'Keyword(s)' }]
                    ).map(field => (
                      <Table.HeaderCell
                        key={field.dataKey}
                        minWidth="180px"
                        flex="1"
                        textAlign="left"
                      >
                        {field.label}
                      </Table.HeaderCell>
                    ))}
                    <Table.HeaderCell flex="1" textAlign="left">
                      Created on
                    </Table.HeaderCell>
                    <Table.HeaderCell flex="1" textAlign="left">
                      Last Edited on
                    </Table.HeaderCell>
                    <Table.HeaderCell width={20} textAlign="right">
                      Actions
                    </Table.HeaderCell>
                  </Table.HeaderRow>
                  <Table.Body>
                    {journal.keywords.length ? (
                      journal.keywords.map(keyword => (
                        <Table.Row key={keyword.id}>
                          {(journal.keywordFields?.length
                            ? journal.keywordFields
                            : [{ dataKey: 'keyword', label: 'Keyword(s)' }]
                          ).map(field => (
                            <Table.RowCell
                              key={field.dataKey}
                              minWidth="180px"
                              flex="1"
                              textAlign="left"
                            >
                              <Text textStyle="textContext">
                                {getKeywordFieldDisplayValue(
                                  keyword,
                                  field.dataKey
                                )}
                              </Text>
                            </Table.RowCell>
                          ))}
                          <Table.RowCell flex="1" textAlign="left">
                            <Text textStyle="textContext">
                              {formatDate(keyword.created_at)}
                            </Text>
                            {keyword.createdByName && (
                              <Text textStyle="textContext">
                                by {keyword.createdByName}
                              </Text>
                            )}
                          </Table.RowCell>
                          <Table.RowCell flex="1" textAlign="left">
                            <Text textStyle="textContext">
                              {formatDate(keyword.updated_at)}
                            </Text>
                            {keyword.updatedByName && (
                              <Text textStyle="textContext">
                                by {keyword.updatedByName}
                              </Text>
                            )}
                          </Table.RowCell>
                          <Table.RowCell width={20} textAlign="right">
                            <Flex justifyContent="flex-end" gap={2}>
                              <ButtonIcon
                                ariaLabel="Edit journal-specific keyword"
                                icon={<FontAwesomeIcon icon={faEdit} />}
                                onClick={() =>
                                  setModalState({
                                    isOpen: true,
                                    mode: 'edit',
                                    journal,
                                    row: keyword
                                  })
                                }
                              />
                              <ButtonIcon
                                ariaLabel="Delete journal-specific keyword"
                                icon={<FontAwesomeIcon icon={faTrashCan} />}
                                onClick={() =>
                                  handleDeleteRow(journal, keyword)
                                }
                                disabled={deleteMutation.isLoading}
                                isDisabled={deleteMutation.isLoading}
                              />
                            </Flex>
                          </Table.RowCell>
                        </Table.Row>
                      ))
                    ) : (
                      <Table.Row>
                        <Table.RowCell>
                          <Text textStyle="textContext">
                            No journal-specific specialty & keywords added.
                          </Text>
                        </Table.RowCell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              </Box>
            </Box>
          ))}
        </Flex>
      )}
      {modalState.isOpen && modalState.journal && (
        <JournalSpecificKeywordModal
          userId={userId}
          state={modalState}
          onClose={() => setModalState({ isOpen: false, mode: 'add' })}
          onSaved={() => {}}
        />
      )}
    </>
  );
};

export default JournalSpecificKeywordsSection;
